import { ERentioFormField } from "@rentiohq/shared-frontend/dist/components/components/Form";
import { IUiSchema } from "@rentiohq/shared-frontend/dist/forms/forms.types";
import { getValue } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import {
  EPropertyEPCLabel,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { getAvailableEpcLabelsForProperty } from "@rentiohq/shared-frontend/dist/utils/property.utils";
import { JSONSchema6 } from "json-schema";
import { FormValidation } from "react-native-jsonschema-form-test";
import { ts } from "../../services";
import { EField, IExtraData } from "./schema.editPropertyInfo.types";

// JSON Schema field augmented with the custom `options` array consumed by the
// SegmentedControl renderer — `options` is not part of the base JSONSchema6.
type ISchemaFieldWithOptions = JSONSchema6 & {
  options: { id: boolean; value: string }[];
};

/**
 * Computes a concrete listing URL example for the placeholder text,
 * using the same substitution logic as rentmatchPreference.repo.ts:
 *   [ID]   → property CRM reference (only shown when a ref is available)
 *   [city] → property city (lowercase)
 *   [ZIP]  → property zip
 *
 * Priority: resolved template URL > websiteUrl > generic i18n placeholder
 * When no CRM ref is known the template is NOT shown (would confuse the broker
 * by displaying a literal "[ID]" in the placeholder).
 */
function computeListingUrlPlaceholder(
  listingUrlTemplate: string | null | undefined,
  websiteUrl: string | null | undefined,
  property: IProperty,
): string {
  if (listingUrlTemplate) {
    // Same COALESCE order as the backend (rentmatchPreference.repo.ts):
    // refWhise → refOmnicasa → refZabun → refDewaele → refSkarabee →
    // refSweepbright → refMaxImmo → refRealSuite
    const crmRef =
      property.refWhise ??
      property.refOmnicasa ??
      property.refZabun ??
      property.refDewaele ??
      property.refSkarabee ??
      property.refSweepbright ??
      property.refMaxImmo ??
      property.refRealSuite ??
      undefined;

    if (crmRef) {
      return listingUrlTemplate
        .replace(/\[ID\]/g, crmRef)
        .replace(/\[city\]/g, (property.city || "").toLowerCase())
        .replace(/\[CITY\]/g, (property.city || "").toLowerCase())
        .replace(/\[ZIP\]/g, property.zip || "");
    }
    // No CRM ref → fall through to websiteUrl or generic placeholder
  }
  if (websiteUrl) {
    return websiteUrl;
  }
  return getLocalizedText(
    "property_info.form.external_listing_url.placeholder",
  );
}

export const getSchema =
  (initialValues: { [key: string]: any }, extraData?: IExtraData) =>
  (defaultValues: { [key: string]: any }): JSONSchema6 => ({
    required: [],
    properties: {
      [EField.Price]: {
        type: ["number", "null"],
        default: getValue(EField.Price, initialValues, defaultValues),
      },
      [EField.Costs]: {
        type: ["number", "null"],
        default: getValue(EField.Costs, initialValues, defaultValues),
      },
      ...(extraData?.hasRentMatchForBroker && {
        [EField.RentMatchIntro]: {
          default: undefined,
        },
        [EField.RoomCountBedroom]: {
          type: ["number", "null"],
          default: getValue(
            EField.RoomCountBedroom,
            initialValues,
            defaultValues,
          ),
        },
        [EField.AvailableAtDate]: {
          default: getValue(
            EField.AvailableAtDate,
            initialValues,
            defaultValues,
          ),
        },
        [EField.IsRentMatchPublished]: {
          type: ["boolean", "null"],
          default: getValue(
            EField.IsRentMatchPublished,
            initialValues,
            defaultValues,
          ),
          options: [
            {
              id: false,
              value: getLocalizedText(
                "property_info.form.is_rent_match_published.option_do_not_publish",
              ),
            },
            {
              id: true,
              value: getLocalizedText(
                "property_info.form.is_rent_match_published.option_publish",
              ),
            },
          ],
        } as ISchemaFieldWithOptions,
        [EField.ExternalListingUrl]: {
          type: ["string", "null"],
          default: getValue(
            EField.ExternalListingUrl,
            initialValues,
            defaultValues,
          ),
        },
        [EField.RoomCountBathroom]: {
          type: ["number", "null"],
          default: getValue(
            EField.RoomCountBathroom,
            initialValues,
            defaultValues,
          ),
        },
        [EField.RoomCountGarage]: {
          type: ["number", "null"],
          default: getValue(
            EField.RoomCountGarage,
            initialValues,
            defaultValues,
          ),
        },
        [EField.BuildingSurfaceArea]: {
          type: ["number", "null"],
          default: getValue(
            EField.BuildingSurfaceArea,
            initialValues,
            defaultValues,
          ),
        },
        [EField.EpcLabel]: {
          default: getValue(EField.EpcLabel, initialValues, defaultValues),
          // @ts-ignore — options is a custom extension used by InlineSelect
          options: getAvailableEpcLabelsForProperty({}),
        },
        [EField.BuildingTerraceSurfaceArea]: {
          type: ["number", "null"],
          default: getValue(
            EField.BuildingTerraceSurfaceArea,
            initialValues,
            defaultValues,
          ),
        },
        [EField.BuildingGardenSurfaceArea]: {
          type: ["number", "null"],
          default: getValue(
            EField.BuildingGardenSurfaceArea,
            initialValues,
            defaultValues,
          ),
        },
      }),
    },
  });

export const uiSchema = ({
  hasRentMatchForBroker,
  listingUrlTemplate,
  websiteUrl,
  property,
}: IExtraData): IUiSchema<EField> => ({
  "rentio:navigationTitle": hasRentMatchForBroker
    ? getLocalizedText("property_info.rentmatch_form.title")
    : ts.propertyInfoFormModalTitle(),
  ...(hasRentMatchForBroker && {
    "rentio:navigationSubTitle": getLocalizedText(
      "property_info.rentmatch_form.extra_tab_hint",
    ),
  }),
  ...(!hasRentMatchForBroker && {
    "ui:order": [EField.Price, EField.Costs],
  }),
  ...(hasRentMatchForBroker && {
    "ui:field": ERentioFormField.Grid,
    grid: {
      row: [
        // Intro: short explanation of what Rentmatch is
        {
          col: { width: [1, 1], children: [EField.RentMatchIntro] },
        },
        // Row 1: publish toggle
        {
          col: { width: [1, 0.5], children: [EField.IsRentMatchPublished] },
        },
        {
          col: { width: [1, 0.5], children: [] },
        },
        // Row 2: price + costs
        {
          col: { width: [1, 0.5], children: [EField.Price] },
        },
        {
          col: { width: [1, 0.5], children: [EField.Costs] },
        },
        // Row 3: bedrooms + available date
        {
          col: { width: [1, 0.5], children: [EField.RoomCountBedroom] },
        },
        {
          col: { width: [1, 0.5], children: [EField.AvailableAtDate] },
        },
        // Row 4: property URL (full width)
        {
          col: { width: [1, 1], children: [EField.ExternalListingUrl] },
        },
        // Row 5: bathrooms + garages
        {
          col: { width: [1, 0.5], children: [EField.RoomCountBathroom] },
        },
        {
          col: { width: [1, 0.5], children: [EField.RoomCountGarage] },
        },
        // Row 6: building surface + EPC
        {
          col: { width: [1, 0.5], children: [EField.BuildingSurfaceArea] },
        },
        {
          col: { width: [1, 0.5], children: [EField.EpcLabel] },
        },
        // Row 7: terrace + garden
        {
          col: {
            width: [1, 0.5],
            children: [EField.BuildingTerraceSurfaceArea],
          },
        },
        {
          col: {
            width: [1, 0.5],
            children: [EField.BuildingGardenSurfaceArea],
          },
        },
      ],
    },
  }),
  [EField.RoomCountBedroom]: {
    "ui:field": ERentioFormField.Number,
    title: getLocalizedText("property.extra.interior.field.room_count_bedroom"),
  },
  [EField.AvailableAtDate]: {
    "ui:field": ERentioFormField.Date,
    title: getLocalizedText("property_info.form.available_at_date.label"),
  },
  [EField.Price]: {
    "ui:field": ERentioFormField.Number,
    formatSummary: (formData?: number) => formData && formatCurrency(formData),
    title: ts.propertyInfoFormPriceLabel(),
    placeholder: "0",
    suffixText: "EUR",
    isFloat: true,
  },
  [EField.Costs]: {
    "ui:field": ERentioFormField.Number,
    formatSummary: (formData?: number) => formData && formatCurrency(formData),
    title: ts.propertyInfoFormCostsLabel(),
    placeholder: "0",
    suffixText: "EUR",
    isFloat: true,
  },
  [EField.RentMatchIntro]: {
    "ui:field": ERentioFormField.HelpInfo,
    showCloseButton: false,
    subtitle: getLocalizedText("property_info.rentmatch_form.intro"),
    infoUrl: "https://rentmatch.be",
    infoLabel: getLocalizedText("system.more_info"),
  },
  [EField.IsRentMatchPublished]: {
    "ui:field": ERentioFormField.SegmentedControl,
    title: getLocalizedText("property_info.form.is_rent_match_published.label"),
  },
  [EField.ExternalListingUrl]: {
    "ui:field": ERentioFormField.Text,
    title: getLocalizedText("property_info.form.external_listing_url.label"),
    placeholder: computeListingUrlPlaceholder(
      listingUrlTemplate,
      websiteUrl,
      property,
    ),
    optional: true,
  },
  // Extra property details (same data as /extra page, accessible here for convenience)
  [EField.RoomCountBathroom]: {
    "ui:field": ERentioFormField.Number,
    title: getLocalizedText(
      "property.extra.interior.field.room_count_bathroom",
    ),
    optional: true,
  },
  [EField.RoomCountGarage]: {
    "ui:field": ERentioFormField.Number,
    title: getLocalizedText("property.extra.interior.field.room_count_garage"),
    optional: true,
  },
  [EField.BuildingSurfaceArea]: {
    "ui:field": ERentioFormField.Number,
    title: getLocalizedText(
      "property.extra.exterior.field.building_surface_area",
    ),
    suffixText: "m²",
    isFloat: true,
    optional: true,
  },
  [EField.EpcLabel]: {
    "ui:field": ERentioFormField.DropdownSelect,
    title: getLocalizedText("editor.variable.property_epc_label"),
    formatSummary: (value: EPropertyEPCLabel) =>
      getLocalizedText(`epc.label.${value}`.toLowerCase()),
    optional: true,
  },
  [EField.BuildingTerraceSurfaceArea]: {
    "ui:field": ERentioFormField.Number,
    title: getLocalizedText(
      "property.extra.exterior.field.building_terrace_surface_area",
    ),
    suffixText: "m²",
    isFloat: true,
    optional: true,
  },
  [EField.BuildingGardenSurfaceArea]: {
    "ui:field": ERentioFormField.Number,
    title: getLocalizedText(
      "property.extra.exterior.field.building_garden_surface_area",
    ),
    suffixText: "m²",
    isFloat: true,
    optional: true,
  },
});

// Returns true when a number field is considered "empty" or zero.
// Handles: null, undefined, "", 0 (number), "0" (string from placeholder),
// and any other value that coerces to 0 or NaN via parseFloat.
function isPriceInvalid(value: any): boolean {
  if (value === null || value === undefined || value === "") return true;
  const num = parseFloat(String(value));
  return isNaN(num) || num <= 0;
}

function isCostsEmpty(value: any): boolean {
  if (value === null || value === undefined || value === "") return true;
  const num = parseFloat(String(value));
  return isNaN(num);
}

export const getValidate =
  ({ hasRentMatchForBroker }: IExtraData) =>
  (formData: any, errors: FormValidation) => {
    // The publish choice is an explicit, required decision for RentMatch
    // brokers (user test finding: a subtle toggle was easy to overlook).
    if (hasRentMatchForBroker) {
      const publishChoice = formData[EField.IsRentMatchPublished];
      if (publishChoice == null) {
        errors[EField.IsRentMatchPublished]?.addError(
          getLocalizedText("system.form.error.required"),
        );
      }
    }

    if (hasRentMatchForBroker && formData[EField.IsRentMatchPublished]) {
      if (isPriceInvalid(formData[EField.Price])) {
        errors[EField.Price]?.addError(
          getLocalizedText("system.form.error.required"),
        );
      }

      if (isCostsEmpty(formData[EField.Costs])) {
        errors[EField.Costs]?.addError(
          getLocalizedText("system.form.error.required"),
        );
      }

      if (
        formData[EField.RoomCountBedroom] === undefined ||
        formData[EField.RoomCountBedroom] === null ||
        formData[EField.RoomCountBedroom] === ""
      ) {
        errors[EField.RoomCountBedroom]?.addError(
          getLocalizedText("system.form.error.required"),
        );
      }

      if (!formData[EField.AvailableAtDate]) {
        errors[EField.AvailableAtDate]?.addError(
          getLocalizedText("system.form.error.required"),
        );
      }
    }

    return errors;
  };
